import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Service for per-exercise, per-day workout notes -- written during an
 * active mission ("how did Bench feel today?") and viewable/editable later
 * from "Previously..." or the exercise history ("i") popup.
 *
 * One note per (profile, exercise, calendar day): a deterministic document
 * id (`${profileId}_${exerciseId}_${dayKey}`, dayKey from groupLogs.js's
 * dayKeyFor -- the same key every day-grouped view in this app already
 * uses) means reads/writes are plain getDoc/setDoc by id, never a query --
 * no composite index needed, and a note for a given day is always a single,
 * unambiguous upsert target regardless of how many sets were logged that day.
 */
const noteDocId = (profileId, exerciseId, dayKey) => `${profileId}_${exerciseId}_${dayKey}`;

// "" when no note has been written for that exercise/day yet -- callers
// don't need to distinguish "never written" from "written then cleared",
// both read back the same way.
export const fetchExerciseNote = async (ownerUserId, profileId, exerciseId, dayKey) => {
  const snap = await getDoc(doc(db, "exerciseNotes", noteDocId(profileId, exerciseId, dayKey)));
  return snap.exists() ? snap.data().note || "" : "";
};

// Batch variant for views that show several days of one exercise's history
// at once (ExerciseHistoryModal) -- bounded by however many distinct days
// are actually on screen, so plain Promise.all over individual getDocs is
// simpler than standing up a query/index for what's normally a handful of
// reads. Returns { [dayKey]: note } with only the days that have a note.
export const fetchExerciseNotesForDays = async (ownerUserId, profileId, exerciseId, dayKeys) => {
  const entries = await Promise.all(
    dayKeys.map(async (dayKey) => [dayKey, await fetchExerciseNote(ownerUserId, profileId, exerciseId, dayKey)])
  );
  return Object.fromEntries(entries.filter(([, note]) => note));
};

// Upserts the note, or deletes the doc entirely when cleared to blank --
// keeps "has a note for this day" a simple doc-exists check rather than
// leaving empty-string docs lying around.
export const saveExerciseNote = async (ownerUserId, profileId, exerciseId, dayKey, note) => {
  const trimmed = note.trim();
  const ref = doc(db, "exerciseNotes", noteDocId(profileId, exerciseId, dayKey));
  try {
    if (!trimmed) {
      await deleteDoc(ref);
    } else {
      await setDoc(ref, {
        ownerUserId,
        profileId,
        exerciseId,
        dayKey,
        note: trimmed,
        updatedAt: serverTimestamp()
      });
    }
    return { success: true };
  } catch (error) {
    console.error("Note Save Error: ", error);
    return { success: false, error };
  }
};
